import { DropdownMenuItem, FAIcon, ModuleWrapper } from '@/components'
import { PageType } from '@/modules/common/consts'
import type { DetailField } from '@/modules/infringements/components/detail/detail-header'
import {
  DetailPill,
  InfringementDetailHeader,
  WronglyGeneratedNotice,
} from '@/modules/infringements/components/detail/detail-header'
import {
  InfringementActionLogCard,
  InfringementActions,
  infringementActionsPlacement,
  InfringementAttachmentsCard,
  InfringementFollowUpBlocks,
  InfringementStatusPills,
} from '@/modules/infringements/components/infringement-follow-up'
import { useFollowUpStateMutations } from '@/modules/infringements/components/use-follow-up-state'
import { useSeverityBackground } from '@/modules/infringements/util'
import { WronglyGeneratedPrompt } from '@/modules/work-infringements/components'
import type { CountryCode } from '@/utils/countries'
import { format } from '@/utils/date-fns'
import {
  useAdmin,
  useBreadcrumbOverride,
  useOnErrorAuto,
  useParam,
} from '@/utils/hooks'
import { useCountryCodeTranslations } from '@/utils/hooks/useCountryCodeTranslations'
import { useMutation, useQuery } from '@apollo/client'
import { Loader, usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { ResultOf } from 'gql.tada'
import React from 'react'
import { useNavigate } from 'react-router'
import { usePatchTachographInfringement } from '../components'
import { PATCH_TACHOGRAPH_INFRINGEMENT_WRONGLY_GENERATED } from '../mutations'
import { TACHOGRAPH_INFRINGEMENT_QUERY } from '../queries'
import { parseTachographInfringementDetails } from '../tachograph-infringement-details'

type TachographInfringementNode = NonNullable<
  ResultOf<typeof TACHOGRAPH_INFRINGEMENT_QUERY>['tachographInfringement']
>

export const TachographInfringement: React.FC = () => {
  const infringementId = useParam('infringementId')
  const navigate = useNavigate()
  const onErrorAuto = useOnErrorAuto()

  const { data, loading } = useQuery(TACHOGRAPH_INFRINGEMENT_QUERY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: { id: infringementId },
    onCompleted(data) {
      if (!data.tachographInfringement) void navigate('/infringements')
    },
    onError: onErrorAuto(),
  })
  const infringement = data?.tachographInfringement
  useBreadcrumbOverride(
    infringementId,
    infringement
      ? format(new Date(infringement.infringementTimestamp), 'PP')
      : null
  )

  if (loading && !infringement) return <Loader.Spinner />
  if (!infringement) return null
  return <TachographInfringementDetail infringement={infringement} />
}

const TachographInfringementDetail: React.FC<{
  infringement: TachographInfringementNode
}> = ({ infringement }) => {
  const translations = useTranslate({
    title: 'infringements.tachograph-infringement',
    eyebrow: 'infringements.source.tachograph-use',
    timestamp: 'common.timestamp',
    basis: 'common.basis',
    vehicle: 'common.vehicle',
    legalBasis: 'infringements.legal-basis',
    euReference: 'infringements.eu-reference',
    period: 'common.period',
    crossing: 'infringements.border-crossing',
    duration: 'common.duration',
    nMinutes: ['common.n-minutes', { n: 0 }],
    archivedBasis: 'infringements.archived-basis',
    archivedDate: 'infringements.archived-date',
    wronglyGeneratedNotice: 'infringements.wrongly-generated-notice',
    wronglyGeneratedExplanation: 'infringements.wrongly-generated-explanation',
    markWronglyGenerated: 'infringements.mark-wrongly-generated',
    unmarkWronglyGenerated: 'infringements.unmark-wrongly-generated',
    severities: {
      MINOR: 'infringements.minor-infringement',
      SERIOUS: 'infringements.serious-infringement',
      VERY_SERIOUS: 'infringements.very-serious-infringement',
      MOST_SERIOUS: 'infringements.most-serious-infringement',
    },
  })
  const onErrorAuto = useOnErrorAuto()
  const isAdmin = useAdmin()
  const addPrompt = usePrompt()
  const getSeverityBackgroundColor = useSeverityBackground()
  const { getCountryCodeTranslations } = useCountryCodeTranslations()
  const { onPatch, loading } = usePatchTachographInfringement(infringement.id)
  const state = useFollowUpStateMutations()

  const [patchWronglyGenerated, { loading: wronglyGeneratedLoading }] =
    useMutation(PATCH_TACHOGRAPH_INFRINGEMENT_WRONGLY_GENERATED, {
      awaitRefetchQueries: true,
      refetchQueries: ['AllCombinedInfringements'],
      onError: onErrorAuto(),
    })

  async function handleToggleWronglyGenerated() {
    if (infringement.wronglyGenerated) {
      await patchWronglyGenerated({
        variables: { id: infringement.id, wronglyGenerated: false },
      })
      return
    }
    const { data: explanation } = await addPrompt<string | null>(resolve => (
      <WronglyGeneratedPrompt onSubmit={resolve} />
    ))
    if (!explanation) return
    await patchWronglyGenerated({
      variables: { id: infringement.id, wronglyGenerated: true, explanation },
    })
  }

  const category = infringement.category
  const details = parseTachographInfringementDetails(infringement.details)
  const period = `${format(
    new Date(infringement.periodStartDate),
    'PP HH:mm'
  )} - ${format(new Date(infringement.periodEndDate), 'PP HH:mm')}`
  const vehicleText =
    details.vehicleBefore &&
    details.vehicleAfter &&
    details.vehicleBefore !== details.vehicleAfter
      ? `${details.vehicleBefore} → ${details.vehicleAfter}`
      : infringement.vehicleRegistrationNumber
  const countryName = (iso: string | undefined, name: string | undefined) =>
    (iso && getCountryCodeTranslations(iso as CountryCode)) || name
  const crossingText = details.countryEntered
    ? `${countryName(details.countryLeftIso, details.countryLeft) ?? '-'} → ${
        countryName(details.countryEnteredIso, details.countryEntered) ?? '-'
      }`
    : null
  const measured =
    crossingText ??
    (details.minutes !== undefined
      ? translations.nMinutes({ n: details.minutes })
      : null)

  const fields: DetailField[] = [
    {
      label: translations.timestamp,
      value: format(
        new Date(infringement.infringementTimestamp),
        'dd.MM.yyyy HH:mm'
      ),
    },
    { label: translations.vehicle, value: vehicleText },
    {
      label: crossingText ? translations.crossing : translations.duration,
      value: measured,
    },
  ]
  if (infringement.archivedBasis) {
    fields.push({
      label: translations.archivedBasis,
      value: infringement.archivedBasis,
    })
  }
  const info: DetailField[] = [
    { label: translations.basis, value: category.description },
    { label: translations.period, value: period },
    {
      label: translations.legalBasis,
      value: category.legalBasis,
      hint: category.euReference
        ? `${translations.euReference}: ${category.euReference}`
        : undefined,
    },
  ]
  if (infringement.archivedAt) {
    info.push({
      label: translations.archivedDate,
      value: format(new Date(infringement.archivedAt), 'PP HH:mm'),
    })
  }

  return (
    <ModuleWrapper
      title={translations.title}
      page={PageType.INFRINGEMENTS}
      {...infringementActionsPlacement(
        <InfringementActions
          closed={!!infringement.closedAt}
          archived={!!infringement.archivedAt}
          busy={loading || state.loading || wronglyGeneratedLoading}
          onSetClosed={closed =>
            state.setClosed('TACHOGRAPH_USE', infringement.id, closed)
          }
          onSetArchived={(archived, basis) =>
            state.setArchived(
              'TACHOGRAPH_USE',
              infringement.id,
              archived,
              basis
            )
          }
          menuItems={
            isAdmin && (
              <DropdownMenuItem
                onClick={() => void handleToggleWronglyGenerated()}
              >
                <FAIcon icon="flag" fixedWidth />
                {infringement.wronglyGenerated
                  ? translations.unmarkWronglyGenerated
                  : translations.markWronglyGenerated}
              </DropdownMenuItem>
            )
          }
        />
      )}
    >
      <div className="w-full space-y-6">
        {infringement.wronglyGenerated && (
          <WronglyGeneratedNotice
            title={translations.wronglyGeneratedNotice}
            explanationLabel={translations.wronglyGeneratedExplanation}
            explanation={infringement.wronglyGeneratedExplanation}
          />
        )}

        <InfringementDetailHeader
          eyebrow={translations.eyebrow}
          title={infringement.user.fullName}
          rule={`${category.identifier} · ${category.title}`}
          pills={
            <>
              <InfringementStatusPills infringement={infringement} />
              <DetailPill
                tone="stone"
                dotColor={getSeverityBackgroundColor(category.severity)}
              >
                {translations.severities[category.severity]}
              </DetailPill>
            </>
          }
          fields={fields}
          info={info}
        >
          <InfringementFollowUpBlocks
            infringement={infringement}
            onPatch={onPatch}
          />
        </InfringementDetailHeader>

        <InfringementAttachmentsCard
          infringement={infringement}
          onPatch={onPatch}
        />

        <InfringementActionLogCard actionLogs={infringement.actionLogs} />
      </div>
    </ModuleWrapper>
  )
}
